import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ErrorState, LoadingState, PayoutHeader, PayoutItemsAgGridTable } from '../';
import { useAgGridColumns, usePayoutDetail, usePayoutItems } from '../../hooks';
import { FiltersSection } from '../interaction';

export const Main = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('codPayoutDetail');
  const payoutId = id ? parseInt(id, 10) : 0;

  // 🎯 Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const {
    payout,
    isLoading: isLoadingPayout,
    isFetching: isFetchingPayout,
    error: errorPayout,
    refetch: refetchPayout,
  } = usePayoutDetail({
    payoutId,
    enabled: !!payoutId,
  });

  const {
    items,
    meta,
    isLoading: isLoadingItems,
    isFetching: isFetchingItems,
    error: errorItems,
    updateFilters,
    handleSortingChange,
    filters,
    sorting,
    hasFilters,
    refetch: refetchItems,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePayoutItems({
    payoutId,
    enabled: !!payoutId,
  });

  const { columnDefs, getColumnsInOrder } = useAgGridColumns();

  // 🎯 Efecto para marcar que la carga inicial terminó
  useEffect(() => {
    if (!isLoadingPayout && !isLoadingItems && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoadingPayout, isLoadingItems, isInitialLoad]);

  // Configurar breadcrumbs para la topbar
  const breadcrumbs = useMemo(
    () => [
      { label: t('breadcrumb.payouts', 'Liquidaciones COD'), path: '/cod-payments/payouts' },
      { label: `${t('breadcrumb.detail', 'Liquidación')} #${payoutId}` },
    ],
    [t, payoutId],
  );

  // Establecer breadcrumbs en la topbar
  usePageNavigation({
    breadcrumbs,
  });

  // Obtener columnas para exportar
  const exportColumns = useMemo(() => getColumnsInOrder(), [getColumnsInOrder]);

  // 🎯 Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad) {
    return (
      <div className="px-6 pt-4">
        <LoadingState message={t('loading.items', 'Cargando items...')} />
      </div>
    );
  }

  // Estado de error del payout
  if (errorPayout) {
    return (
      <div className="px-6 pt-4">
        <ErrorState message={errorPayout} onRetry={refetchPayout} />
      </div>
    );
  }

  // Si no hay payout después de la carga inicial, mostrar error
  if (!payout) {
    return (
      <div className="px-6 pt-4">
        <ErrorState message={t('error.payout_not_found', 'No se encontró la liquidación')} onRetry={refetchPayout} />
      </div>
    );
  }

  // Total de items para detectar estado vacío
  const total = meta?.total ?? 0;

  return (
    <div className="px-6 pt-4">
      {/* Header con información del payout */}
      <PayoutHeader payout={payout} isFetching={isFetchingPayout} />

      {/* Filtros */}
      <FiltersSection
        filters={filters}
        onFiltersChange={updateFilters}
        isFetching={isFetchingItems}
        payoutId={payoutId}
        exportColumns={exportColumns}
      />

      {/* Tabla de items con AG-Grid */}
      {errorItems ? (
        <ErrorState message={errorItems} onRetry={refetchItems} className="h-40" />
      ) : (
        <>
          {/* 🎯 Estado vacío: siempre en el DOM, controlado por opacity y height */}
          <div
            className={`transition-opacity duration-200 overflow-hidden ${
              total === 0 ? 'opacity-100 h-auto' : 'opacity-0 h-0 pointer-events-none'
            }`}
          >
            <EmptyView
              type={hasFilters ? 'noResults' : 'emptyState'}
              icon={EMPTY_VIEW_ICONS.DEFAULT}
              title={
                hasFilters
                  ? t('empty.no_results_title', 'No se encontraron resultados')
                  : t('empty.title', 'No hay items')
              }
              description={
                hasFilters
                  ? t(
                      'empty.no_results_description',
                      'No hay items que coincidan con los filtros aplicados. Intenta ajustar los filtros.',
                    )
                  : t('empty.description', 'No hay items en esta liquidación')
              }
            />
          </div>

          {/* 🎯 Vista de items: siempre en el DOM, controlado por opacity y height */}
          <div
            className={`mb-4 transition-opacity duration-200 overflow-hidden ${
              total > 0 ? 'opacity-100 h-auto' : 'opacity-0 h-0'
            }`}
          >
            <PayoutItemsAgGridTable
              items={items}
              columnDefs={columnDefs}
              isLoading={isLoadingItems}
              isFetching={isFetchingItems}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
            />
          </div>
        </>
      )}
    </div>
  );
};
